import { useEffect } from "react";
import SymptomResultsSection from "@/components/ResultsPage/SymptomResultsSection/SymptomResultsSection";
import MotivationalMessageSection from "@/components/ResultsPage/MotivationalMessageSection/MotivationalMessageSection";
import BookAppointmentSectionDefault from "@/components/ResultsPage/BookAppointmentSection/BookAppointmentSectionDefault";
import BookAppointmentSectionFemale from "@/components/ResultsPage/BookAppointmentSection/BookAppointmentSectionFemale";
import SleepClinicSection from "@/components/ResultsPage/SleepClinicSection/SleepClinicSection";
import DownloadReportSection from "@/components/ResultsPage/DownloadReportSection/DownloadReportSection";
import ReferencesSection from "@/components/ResultsPage/ReferencesSection/ReferencesSection";
import SocialProofSection from "@/components/ResultsPage/SocialProofSection/SocialProofSection";
import RedefiningSleep from "@/components/ResultsPage/RedefiningSleep/RedefiningSleep";
import MyNightBanner from "@/components/ResultsPage/MyNightBanner/MyNightBanner";
import { useVariant, Variant, PAGE_VARIANT } from "@/hooks/useVariant";
import { useAssessment } from "@/contexts/AssessmentContext/AssessmentContext";
import { fireQuizFinish } from "@/utils/analytics";
import { setResultPageContext, clearResultPageContext } from "@/utils/commonEvents";
import type { SymptomCardConfig } from "@/api/types";

/**
 * Determines the overall result outcome from the symptom cards.
 * The OSA card's riskLabelKey is the primary signal: 'osa.risk-label-high'
 * maps to 'likely', anything else (e.g. 'osa.risk-label-low') maps to 'unlikely'.
 */
function deriveResultOutcome(
  symptomCards: SymptomCardConfig[],
): "likely" | "unlikely" {
  const osaCard = symptomCards.find((c) => c.id === "osa");
  return osaCard?.riskLabelKey === "osa.risk-label-high" ? "likely" : "unlikely";
}

export default function ResultsPage() {
  const variant = useVariant();
  const { answers, assessmentResult } = useAssessment();
  const userNameAnswer = answers.find((a) => a.questionId === "user-name");
  const userName =
    (userNameAnswer?.answerId as Record<string, string>)?.["first-name"] ?? "";
  const symptomCards = assessmentResult?.symptomCards ?? [];
  const resultOutcome = deriveResultOutcome(symptomCards);
  const pageVariant = PAGE_VARIANT[variant];

  // Set CTA analytics context for this page
  useEffect(() => {
    setResultPageContext(pageVariant, resultOutcome);
    return () => clearResultPageContext();
  }, [pageVariant, resultOutcome]);

  // Push sleep_quiz_finish on mount
  useEffect(() => {
    fireQuizFinish(resultOutcome, pageVariant);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fires only on mount; values are stable at result time
  }, []);

  return (
    <div className="results-page">
      {symptomCards.length > 0 && (
            <SymptomResultsSection
              variant={variant}
              userName={userName}
              symptomCards={symptomCards}
            />
      )}
 
      {variant === Variant.DEFAULT ? (
        // Default content ordering
        <>
          <BookAppointmentSectionDefault />
          <MotivationalMessageSection />
          <div className="download-report-section download-report-section--default">
            <DownloadReportSection variant={variant} />
          </div>
          <SleepClinicSection variant={variant} />
          <RedefiningSleep />
          <MyNightBanner />
        </>
      ) : (
        // Female content ordering
        <>
          <div className="download-report-section download-report-section--female">
            <DownloadReportSection variant={variant} />
          </div>
          <BookAppointmentSectionFemale resultOutcome={resultOutcome} />
          <SleepClinicSection variant={variant} />
          <SocialProofSection />
          <RedefiningSleep />
          <MyNightBanner />
        </>
      )}
      <ReferencesSection />
    </div>
  );
}
